/**
 * EightyGConfigCard — Super-admin 80G receipt configuration.
 * Rendered inside /app/settings as a new tab.
 */
import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { FloppyDisk, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";

function TrustIdentitySection({ form, set }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="sm:col-span-2">
        <Label>Trust / Society legal name</Label>
        <Input data-testid="eg-trust-name" value={form.trust_legal_name || ""} onChange={(e) => set("trust_legal_name", e.target.value)} placeholder="Uttar Pradesh Janajati Seva Sansthan Inc." />
      </div>
      <div>
        <Label>Regd. No.</Label>
        <Input data-testid="eg-regd-no" value={form.regd_no || ""} onChange={(e) => set("regd_no", e.target.value)} placeholder="213 of 1989-90" className="font-mono" />
      </div>
      <div>
        <Label>PAN No.</Label>
        <Input data-testid="eg-pan-no" value={form.pan_no || ""} onChange={(e) => set("pan_no", e.target.value.toUpperCase())} placeholder="AAATU1373L" className="font-mono" maxLength={10} />
      </div>
      <div className="sm:col-span-2">
        <Label>80G UR No. / reference</Label>
        <Input data-testid="eg-80g-ref" value={form.eighty_g_ref || ""} onChange={(e) => set("eighty_g_ref", e.target.value)} placeholder="AAATU1373LF20214 Dt. 24/09/21" className="font-mono text-sm" />
      </div>
      <div className="sm:col-span-2">
        <Label>Administrative office</Label>
        <Textarea data-testid="eg-admin-office" rows={2} value={form.admin_office || ""} onChange={(e) => set("admin_office", e.target.value)} placeholder="E-7, Krishan Vihar, Delhi-110086, INDIA. Telephone: 011-43020770" />
      </div>
      <div className="sm:col-span-2">
        <Label>Registered office</Label>
        <Textarea data-testid="eg-regd-office" rows={2} value={form.regd_office || ""} onChange={(e) => set("regd_office", e.target.value)} placeholder="Vill. & P.O. - …, Distt. - …, Pin Code-…" />
      </div>
    </div>
  );
}

function SerialNumberSection({ form, set }) {
  return (
    <div className="vs-card p-4 bg-[var(--vs-bg-subtle)]">
      <div className="eyebrow mb-2">Receipt serial number</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label>Prefix</Label>
          <Input data-testid="eg-prefix" value={form.receipt_prefix || ""} onChange={(e) => set("receipt_prefix", e.target.value)} placeholder="UPJSSI" className="font-mono" />
          <p className="text-[11px] text-[var(--vs-text-secondary)] mt-1">Visible as {(form.receipt_prefix || "PREFIX")}/{form.next_counter || 1}</p>
        </div>
        <div>
          <Label>Counter padding</Label>
          <Input data-testid="eg-padding" type="number" min={0} max={6} value={form.counter_padding || 0} onChange={(e) => set("counter_padding", Number(e.target.value))} className="font-mono" />
          <p className="text-[11px] text-[var(--vs-text-secondary)] mt-1">0 = no padding (1102). 4 = "0001".</p>
        </div>
        <div>
          <Label>Next counter</Label>
          <Input data-testid="eg-next-counter" type="number" min={1} value={form.next_counter || 1} onChange={(e) => set("next_counter", Number(e.target.value))} className="font-mono" />
          <p className="text-[11px] text-[var(--vs-text-secondary)] mt-1">Seed this when migrating from a paper book.</p>
        </div>
      </div>
    </div>
  );
}

function SignatoriesSection({ form, set }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div>
        <Label>President name</Label>
        <Input data-testid="eg-sig-pres" value={form.signatory_president || ""} onChange={(e) => set("signatory_president", e.target.value)} />
      </div>
      <div>
        <Label>Treasurer name</Label>
        <Input data-testid="eg-sig-treas" value={form.signatory_treasurer || ""} onChange={(e) => set("signatory_treasurer", e.target.value)} />
      </div>
      <div>
        <Label>Collector name</Label>
        <Input data-testid="eg-sig-coll" value={form.signatory_collector || ""} onChange={(e) => set("signatory_collector", e.target.value)} />
      </div>
    </div>
  );
}

export function EightyGConfigCard() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => api.get("/admin/settings/80g").then((r) => setForm(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const activities = typeof form.activities === "string"
        ? form.activities.split(",").map((s) => s.trim()).filter(Boolean)
        : form.activities;
      await api.put("/admin/settings/80g", { ...form, activities });
      toast.success("80G config saved");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!form) return <div className="vs-card p-6 max-w-3xl">Loading…</div>;

  const activitiesText = Array.isArray(form.activities) ? form.activities.join(", ") : (form.activities || "");

  return (
    <div className="vs-card p-6 max-w-3xl space-y-5" data-testid="eighty-g-settings-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow flex items-center gap-1.5"><Receipt size={11} /> 80G receipt config</div>
          <p className="text-sm text-[var(--vs-text-secondary)] mt-1">
            Branding, registration info, signatories, and receipt serial-number scheme.
            Used by every 80G PDF generated for donors.
          </p>
        </div>
        <Badge variant="outline" data-testid="eighty-g-fy-badge">FY {form.current_fy}</Badge>
      </div>

      <TrustIdentitySection form={form} set={set} />
      <SerialNumberSection form={form} set={set} />
      <SignatoriesSection form={form} set={set} />

      <div>
        <Label>Activities (comma-separated)</Label>
        <Textarea data-testid="eg-activities" rows={3} value={activitiesText} onChange={(e) => set("activities", e.target.value)} placeholder="Hostels, Ashrams, Schools, Coaching Centres, …" />
      </div>

      <Button onClick={save} disabled={busy} data-testid="eg-save" className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]">
        <FloppyDisk size={14} className="mr-1.5" /> Save 80G config
      </Button>
    </div>
  );
}

export default EightyGConfigCard;
